import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import uniq from 'lodash/uniq'

import getMultiFilters from 'modules/core/components/MultiFilters/hooks/utils/getMultiFilters'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import asyncAction, { isSuccessful } from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import {
  formatCustomFilters,
  getCustomFilters,
} from 'modules/shift_management/components/ShiftManagement/Content/_filters/customFilters'
import useApplyFilters from 'modules/shift_management/hooks/useApplyFilters'
import { useLoadBootstrapData } from 'modules/shift_management/hooks/useFetchEmployees'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUI } from 'modules/shift_management/hooks/useUI'
import Api from 'modules/shift_management/lib/Api'
import { ADD_SHIFT_OPTIONS } from 'modules/shift_management/lib/addShiftOptions'
import { nameFromHexCode } from 'modules/shift_management/lib/colors'
import { trackAddShiftSubmitEvent } from 'modules/shift_management/lib/trackEvents'
import type { ShiftPayload } from 'modules/shift_management/store/internal/models/Shift'
import type View from 'modules/shift_management/store/internal/models/View'

interface Props {
  originalView: View
  targetView: View
  contractVersions: ContractVersions
}

const useCopySchedule = ({ originalView, targetView, contractVersions }: Props) => {
  const [{ shiftsRows }] = useShiftsRows()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [, dispatchPagination] = usePagination()
  const [, dispatchUI] = useUI()

  const { loadBootstrapData } = useLoadBootstrapData(contractVersions)

  const { getFilteredEmployees } = useApplyFilters(shiftsRows, contractVersions)

  const { running: submitting, performAction: onConfirm } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      const employeeIds = getFilteredEmployees().map((e) => e.id)

      const { filters: multiFilters, logicOperator } = getMultiFilters(
        LOCAL_STORAGE_KEYS.shiftEmployees
      )

      const custom_filters = getCustomFilters(multiFilters)

      const result = await asyncAction(
        () =>
          Api.copySchedule(originalView.startAt, targetView.startAt, {
            employee_ids: employeeIds,
            logic_operator: logicOperator.get('logic_operator').value,
            custom_filters: formatCustomFilters(custom_filters),
          }),
        {
          successFlash: true,
          errorFlash: true,
        }
      )

      if (isSuccessful(result)) {
        const { value: schedule } = result
        const copiedShifts = schedule.shifts
        const copiedRestDays = schedule.rest_days
        const impactedEmployees: number[] = uniq(
          copiedShifts.map((s: ShiftPayload) => s.employee_id)
        )
        const hexCodes = copiedShifts.map((shift: ShiftPayload) => shift.color)
        const uniqHexCodes = uniq(hexCodes)
        const color = uniqHexCodes.map((code) => (code ? nameFromHexCode(code) : null))

        trackAddShiftSubmitEvent({
          view,
          employeeIds: impactedEmployees,
          shiftsCount: copiedShifts.length,
          restDaysCount: copiedRestDays.length,
          shiftType: ADD_SHIFT_OPTIONS.copyPeriod,
          color,
          from: 'copy_period',
        })

        dispatchUI({ type: 'LOADING' })
        dispatchSchedule({ type: 'RESET_VIEW', view })
        dispatchPagination({ type: 'RESET_PAGINATION' })

        loadBootstrapData(targetView)
      }

      closePortal()
    },
    {
      successMessage: i18n.t('shift_management.actions.copy_shifts_success'),
      errorFlash: false,
    }
  )

  return {
    submitting,
    onConfirm,
  }
}

export default useCopySchedule
